import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Calendar,
  CheckCircle2,
  XCircle,
  Users,
  Search,
  Loader2,
  AlertTriangle,
  RefreshCw,
  Pencil,
  Save,
  X,
  CheckSquare,
  XSquare,
} from "lucide-react";
import { useStaffAuth } from "../../context/StaffAuthContext";
import {
  getMyClassAttendance,
  markAttendance,
  editAttendance,
} from "../../api/studentattendance/studentAttendancePortalApi";
import { Card, CardContent, Badge, Button, IconButton, Alert, Skeleton } from "../../components/ui";
import { useToast } from "../../context/ToastContext";

const getTodayString = () => {
  const today = new Date();
  return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
};

const STATUS_BADGE_VARIANTS = {
  PRESENT: "success",
  ABSENT: "danger",
  LATE: "warning",
  HOLIDAY: "primary",
  LEAVE: "purple",
  NOT_MARKED: "secondary",
};

const MarkAttendance = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { classTeacherSections, isClassTeacher } = useStaffAuth();

  const initialSection =
    searchParams.get("sectionId") ||
    classTeacherSections?.[0]?._id ||
    "";

  const [activeSectionId, setActiveSectionId] = useState(initialSection);
  const toast = useToast();

  const [date, setDate] = useState(getTodayString());
  const [mode, setMode] = useState("ABSENT_LIST");
  const [selectedStudentIds, setSelectedStudentIds] = useState([]);
  const [attendanceData, setAttendanceData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [search, setSearch] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editStatus, setEditStatus] = useState("PRESENT");
  const [editRemark, setEditRemark] = useState("");
  const [forceRemake, setForceRemake] = useState(false);

  const loadAttendance = useCallback(async () => {
    if (!activeSectionId) return;
    setLoading(true);
    try {
      const data = await getMyClassAttendance(activeSectionId, date);
      setAttendanceData(data);
      setSelectedStudentIds([]);
      setForceRemake(false);
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || "Failed to load attendance");
      setAttendanceData(null);
    } finally {
      setLoading(false);
    }
  }, [activeSectionId, date]);

  useEffect(() => {
    if (isClassTeacher && activeSectionId) {
      loadAttendance();
    } else {
      setLoading(false);
    }
  }, [isClassTeacher, activeSectionId, date, loadAttendance]);

  if (!isClassTeacher) {
    return (
      <div className="p-4 sm:p-6 max-w-4xl mx-auto">
        <Alert variant="danger" className="p-6 rounded-2xl text-center shadow-xs">
          <p className="text-sm font-extrabold">You are not assigned as a class teacher.</p>
        </Alert>
      </div>
    );
  }

  const students = attendanceData?.students || [];
  const isMarked = attendanceData?.isMarked && !forceRemake;
  const dayType = attendanceData?.dayType || "WORKING";
  const isHoliday = dayType === "HOLIDAY";

  const filteredStudents = students.filter((s) => {
    const name = (
      s.name ||
      `${s.firstName || ""} ${s.lastName || ""}` ||
      ""
    ).toLowerCase();
    const adm = (s.admissionNo || "").toLowerCase();
    const q = search.toLowerCase();
    return name.includes(q) || adm.includes(q);
  });

  const getStudentId = (s) =>
    String(s.studentId?._id || s.studentId || s._id);

  const toggleStudent = (id) => {
    setSelectedStudentIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const selectAll = () => {
    setSelectedStudentIds(filteredStudents.map(getStudentId));
  };

  const deselectAll = () => {
    setSelectedStudentIds([]);
  };

  const handleMark = async () => {
    if (!activeSectionId || isHoliday) return;
    setSubmitting(true);
    try {
      const res = await markAttendance({
        sectionId: activeSectionId,
        date,
        mode,
        studentIds: selectedStudentIds,
      });
      toast.success(
        `Attendance marked — ${res.presentCount} Present, ${res.absentCount} Absent`
      );
      setForceRemake(false);
      await loadAttendance();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to mark attendance");
    } finally {
      setSubmitting(false);
    }
  };

  const startEdit = (s) => {
    const id = getStudentId(s);
    setEditingId(id);
    setEditStatus(s.status === "NOT_MARKED" ? "PRESENT" : s.status);
    setEditRemark(s.remark || "");
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditStatus("PRESENT");
    setEditRemark("");
  };

  const saveEdit = async () => {
    if (!editingId) return;
    setSubmitting(true);
    try {
      await editAttendance(date, {
        sectionId: activeSectionId,
        updates: [
          {
            studentId: editingId,
            status: editStatus,
            remark: editRemark,
          },
        ],
      });
      toast.success("Attendance updated");
      cancelEdit();
      await loadAttendance();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to update");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="p-4 sm:p-6 max-w-6xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pb-4 border-b border-neutral-200 dark:border-neutral-800/80">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900 dark:text-neutral-100 tracking-tight">Mark Attendance</h1>
          <p className="text-xs sm:text-sm font-semibold text-neutral-500 dark:text-neutral-400 mt-1">
            Mark or edit attendance for your class section
          </p>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <div className="flex items-center gap-2 bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800/80 rounded-xl px-3 py-1.5 shadow-2xs">
            <Calendar size={16} className="text-neutral-400 dark:text-neutral-500 shrink-0" />
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="outline-none text-xs font-extrabold text-neutral-700 dark:text-neutral-200 bg-transparent"
            />
          </div>
          <IconButton
            onClick={loadAttendance}
            variant="outline"
            size="sm"
            title="Refresh"
            className="rounded-xl border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 hover:bg-neutral-50 dark:bg-neutral-950 dark:hover:bg-neutral-800 shadow-2xs"
          >
            <RefreshCw size={16} />
          </IconButton>
        </div>
      </div>

      {/* Section switcher */}
      {classTeacherSections?.length > 1 && (
        <div className="flex flex-wrap gap-2">
          {classTeacherSections.map((sec) => {
            const isActive = String(activeSectionId) === String(sec._id);
            return (
              <Button
                key={sec._id}
                onClick={() => setActiveSectionId(sec._id)}
                variant={isActive ? "primary" : "outline"}
                size="sm"
                className={`rounded-xl px-4 py-2 font-extrabold shadow-2xs transition-all ${ isActive ? "bg-indigo-600 text-white" : "border-neutral-200 dark:border-neutral-800 text-neutral-700 dark:text-neutral-200 hover:bg-neutral-50 dark:bg-neutral-950 dark:hover:bg-neutral-800" }`}
              >
                {sec.classId?.className || "Class"} - {sec.sectionName}
              </Button>
            );
          })}
        </div>
      )}

      {/* Banners */}
      
      
      {isHoliday && (
        <Alert variant="danger" className="py-3 px-4 text-xs font-bold rounded-2xl shadow-xs">
          <div className="flex items-center gap-2">
            <XCircle size={16} className="shrink-0" />
            <span>Today is a holiday. Attendance cannot be marked.</span>
          </div>
        </Alert>
      )}
      {dayType === "HALF_DAY" && !isHoliday && (
        <Alert variant="warning" className="py-3 px-4 text-xs font-bold rounded-2xl shadow-xs">
          <span>Half day schedule active today.</span>
        </Alert>
      )}
      {isMarked && (
        <Alert variant="success" className="py-3 px-4 text-xs font-bold rounded-2xl shadow-xs flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <span className="flex items-center gap-2">
            <CheckCircle2 size={16} className="shrink-0" />
            <span>Attendance already marked for this date. You can inline-edit below or re-mark.</span>
          </span>
          <Button
            onClick={() => setForceRemake(true)}
            variant="outline"
            size="xs"
            className="font-bold bg-white dark:bg-neutral-900 text-neutral-800 dark:text-neutral-100 border-neutral-200 dark:border-neutral-800 hover:bg-neutral-50 dark:bg-neutral-950 dark:hover:bg-neutral-800 rounded-lg shrink-0 shadow-2xs"
          >
            Re-mark All
          </Button>
        </Alert>
      )}

      {loading ? (
        <div className="space-y-4">
          <Skeleton height={60} width="100%" className="rounded-2xl" />
          <Skeleton height={200} width="100%" className="rounded-2xl" />
        </div>
      ) : (
        <>
          {/* Summary chips */}
          {attendanceData?.summary && (
            <div className="flex flex-wrap gap-2.5">
              <Badge variant="secondary" className="px-3 py-1 text-xs font-extrabold rounded-full bg-slate-100 dark:bg-neutral-900 text-neutral-700 dark:text-neutral-200">
                Total: {attendanceData.summary.total}
              </Badge>
              <Badge variant="success" className="px-3 py-1 text-xs font-extrabold rounded-full">
                Present: {attendanceData.summary.present}
              </Badge>
              <Badge variant="danger" className="px-3 py-1 text-xs font-extrabold rounded-full">
                Absent: {attendanceData.summary.absent}
              </Badge>
              <Badge variant="warning" className="px-3 py-1 text-xs font-extrabold rounded-full">
                Late: {attendanceData.summary.late}
              </Badge>
              {attendanceData.summary.notMarked > 0 && (
                <Badge variant="secondary" className="px-3 py-1 text-xs font-extrabold rounded-full">
                  Not marked: {attendanceData.summary.notMarked}
                </Badge>
              )}
            </div>
          )}

          {/* MARKING MODE (not yet marked or force re-mark) */}
          {!isMarked && !isHoliday && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setMode("ABSENT_LIST");
                    setSelectedStudentIds([]);
                  }}
                  className={`p-4 rounded-2xl text-left border-2 transition-all shadow-2xs ${ mode === "ABSENT_LIST" ? "bg-rose-500 text-white border-rose-500 shadow-md" : "bg-white dark:bg-neutral-900 border-neutral-200 dark:border-neutral-800 hover:border-rose-300 text-neutral-800 dark:text-neutral-100" }`}
                >
                  <div className="font-extrabold text-sm flex items-center gap-2">
                    <XSquare size={16} /> Enter Absent Students
                  </div>
                  <div
                    className={`text-xs mt-1 font-medium ${
                      mode === "ABSENT_LIST" ? "text-rose-100" : "text-neutral-500 dark:text-neutral-400"
                    }`}
                  >
                    All unselected students will automatically be marked Present
                  </div>
                </button>

                <button
                  type="button"
                  onClick={() => {
                    setMode("PRESENT_LIST");
                    setSelectedStudentIds([]);
                  }}
                  className={`p-4 rounded-2xl text-left border-2 transition-all shadow-2xs ${ mode === "PRESENT_LIST" ? "bg-emerald-500 text-white border-emerald-500 shadow-md" : "bg-white dark:bg-neutral-900 border-neutral-200 dark:border-neutral-800 hover:border-emerald-300 text-neutral-800 dark:text-neutral-100" }`}
                >
                  <div className="font-extrabold text-sm flex items-center gap-2">
                    <CheckSquare size={16} /> Enter Present Students
                  </div>
                  <div
                    className={`text-xs mt-1 font-medium ${
                      mode === "PRESENT_LIST" ? "text-emerald-100" : "text-neutral-500 dark:text-neutral-400"
                    }`}
                  >
                    All unselected students will automatically be marked Absent
                  </div>
                </button>
              </div>

              {/* Search + select all */}
              <Card className="border border-neutral-200 dark:border-neutral-800/80 bg-white dark:bg-neutral-900 rounded-2xl p-4 shadow-xs">
                <div className="flex flex-col sm:flex-row items-center gap-3">
                  <div className="relative flex-1 w-full">
                    <Search
                      size={16}
                      className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400 dark:text-neutral-500"
                    />
                    <input
                      type="text"
                      placeholder="Search by name or admission no..."
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      className="w-full pl-9 pr-3 py-2 border border-neutral-200 dark:border-neutral-800 rounded-xl text-xs font-medium outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 bg-neutral-50/50 dark:bg-neutral-950"
                    />
                  </div>
                  <div className="flex items-center gap-2 w-full sm:w-auto justify-end">
                    <Button
                      onClick={selectAll}
                      variant="outline"
                      size="xs"
                      className="font-bold border-neutral-200 dark:border-neutral-800 rounded-xl hover:bg-neutral-50 dark:bg-neutral-950 dark:hover:bg-neutral-800"
                    >
                      Select All
                    </Button>
                    <Button
                      onClick={deselectAll}
                      variant="outline"
                      size="xs"
                      className="font-bold border-neutral-200 dark:border-neutral-800 rounded-xl hover:bg-neutral-50 dark:bg-neutral-950 dark:hover:bg-neutral-800"
                    >
                      Deselect All
                    </Button>
                  </div>
                </div>

                <div className="mt-3 text-xs font-bold text-neutral-600 dark:text-neutral-300">
                  {selectedStudentIds.length} student
                  {selectedStudentIds.length !== 1 ? "s" : ""} selected for mode ({mode === "ABSENT_LIST" ? "Absent" : "Present"})
                </div>
              </Card>

              {/* Student list with checkboxes */}
              <Card className="border border-neutral-200 dark:border-neutral-800/80 bg-white dark:bg-neutral-900 rounded-2xl overflow-hidden shadow-xs">
                {filteredStudents.length === 0 ? (
                  <div className="p-8 text-center text-xs font-bold text-neutral-500 dark:text-neutral-400">
                    No matching students found
                  </div>
                ) : (
                  <ul className="divide-y divide-slate-100 dark:divide-neutral-800">
                    {filteredStudents.map((s) => {
                      const id = getStudentId(s);
                      const name =
                        s.name ||
                        `${s.firstName || ""} ${s.lastName || ""}`.trim() ||
                        "—";
                      const checked = selectedStudentIds.includes(id);
                      return (
                        <li
                          key={id}
                          className={`flex items-center gap-3 px-4 py-3 transition-colors cursor-pointer ${
                            checked ? "bg-indigo-50/40" : "hover:bg-neutral-50 dark:hover:bg-neutral-800/80"
                          }`}
                          onClick={() => toggleStudent(id)}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleStudent(id)}
                            className="w-4 h-4 rounded border-slate-300 dark:border-neutral-700 text-indigo-600 focus:ring-indigo-500"
                            onClick={(e) => e.stopPropagation()}
                          />
                          <div className="w-8 h-8 rounded-full bg-slate-100 dark:bg-neutral-900 text-neutral-700 dark:text-neutral-200 flex items-center justify-center text-xs font-extrabold">
                            {(name[0] || "?").toUpperCase()}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-xs font-extrabold text-neutral-800 dark:text-neutral-100 truncate">
                              {name}
                            </div>
                            <div className="text-[11px] font-semibold text-neutral-400 dark:text-neutral-500">
                              Adm: {s.admissionNo || "—"}
                              {s.rollNumber ? ` · Roll: ${s.rollNumber}` : ""}
                            </div>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </Card>

              <Button
                onClick={handleMark}
                disabled={submitting || isHoliday}
                variant="primary"
                className="w-full sm:w-auto px-6 py-2.5 rounded-xl font-bold bg-emerald-600 hover:bg-emerald-700 text-white shadow-xs active:scale-95 transition-all"
              >
                {submitting ? (
                  <>
                    <Loader2 className="animate-spin mr-2" size={16} />
                    Saving...
                  </>
                ) : (
                  <>
                    <CheckCircle2 size={16} className="mr-2" />
                    Mark Attendance
                  </>
                )}
              </Button>
            </div>
          )}

          {/* ALREADY MARKED — table + inline edit */}
          {isMarked && (
            <Card className="border border-neutral-200 dark:border-neutral-800/80 bg-white dark:bg-neutral-900 rounded-2xl overflow-hidden shadow-xs">
              <div className="overflow-x-auto">
                <table className="w-full text-xs text-left">
                  <thead className="bg-slate-50/80 text-neutral-600 dark:text-neutral-300 font-extrabold border-b border-neutral-200 dark:border-neutral-800">
                    <tr>
                      <th className="px-4 py-3">Student</th>
                      <th className="px-4 py-3">Admission</th>
                      <th className="px-4 py-3">Roll</th>
                      <th className="px-4 py-3">Status</th>
                      <th className="px-4 py-3">Remark</th>
                      <th className="px-4 py-3 text-right">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 dark:divide-neutral-800">
                    {students.map((s) => {
                      const id = getStudentId(s);
                      const name =
                        s.name ||
                        `${s.firstName || ""} ${s.lastName || ""}`.trim() ||
                        "—";
                      const isEditing = editingId === id;
                      return (
                        <tr key={id} className="hover:bg-neutral-50 dark:hover:bg-neutral-800/60 transition-colors">
                          <td className="px-4 py-3 font-extrabold text-neutral-800 dark:text-neutral-100">
                            {name}
                          </td>
                          <td className="px-4 py-3 text-neutral-500 dark:text-neutral-400 font-semibold">
                            {s.admissionNo || "—"}
                          </td>
                          <td className="px-4 py-3 text-neutral-500 dark:text-neutral-400 font-semibold">
                            {s.rollNumber || "—"}
                          </td>
                          <td className="px-4 py-3">
                            {isEditing ? (
                              <select
                                value={editStatus}
                                onChange={(e) => setEditStatus(e.target.value)}
                                className="border border-neutral-200 dark:border-neutral-800 rounded-lg px-2 py-1 text-xs font-bold text-neutral-800 dark:text-neutral-100 bg-white dark:bg-neutral-900 shadow-2xs"
                              >
                                <option value="PRESENT">PRESENT</option>
                                <option value="ABSENT">ABSENT</option>
                                <option value="LATE">LATE</option>
                                <option value="LEAVE">LEAVE</option>
                              </select>
                            ) : (
                              <Badge
                                variant={STATUS_BADGE_VARIANTS[s.status] || "secondary"}
                                className="px-2.5 py-0.5 text-[10px] font-extrabold rounded-full uppercase"
                              >
                                {s.status}
                              </Badge>
                            )}
                          </td>
                          <td className="px-4 py-3 text-neutral-500 dark:text-neutral-400 font-medium max-w-[180px]">
                            {isEditing ? (
                              <input
                                type="text"
                                value={editRemark}
                                onChange={(e) => setEditRemark(e.target.value)}
                                className="border border-neutral-200 dark:border-neutral-800 rounded-lg px-2 py-1 text-xs font-medium w-full bg-white dark:bg-neutral-900 shadow-2xs"
                                placeholder="Remark"
                              />
                            ) : (
                              <span className="truncate block">
                                {s.remark || "—"}
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-right">
                            {isEditing ? (
                              <div className="flex items-center justify-end gap-1">
                                <IconButton
                                  onClick={saveEdit}
                                  disabled={submitting}
                                  variant="ghost"
                                  size="xs"
                                  className="text-emerald-600 hover:bg-emerald-50 rounded-lg"
                                  title="Save"
                                >
                                  <Save size={14} />
                                </IconButton>
                                <IconButton
                                  onClick={cancelEdit}
                                  variant="ghost"
                                  size="xs"
                                  className="text-neutral-500 dark:text-neutral-400 hover:bg-slate-100 dark:hover:bg-neutral-800 rounded-lg"
                                  title="Cancel"
                                >
                                  <X size={14} />
                                </IconButton>
                              </div>
                            ) : (
                              <IconButton
                                onClick={() => startEdit(s)}
                                variant="ghost"
                                size="xs"
                                className="text-neutral-500 dark:text-neutral-400 hover:bg-slate-100 dark:hover:bg-neutral-800 rounded-lg"
                                title="Edit"
                              >
                                <Pencil size={14} />
                              </IconButton>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </Card>
          )}

          {isHoliday && (
            <div className="text-center py-12 text-xs font-bold text-neutral-400 dark:text-neutral-500">
              Cannot mark attendance on a holiday.
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default MarkAttendance;